import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { markPayablePaid } from '../../services/mutations';
import { Payable } from './useData';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { Button, Label, DateInput, NumberInput } from '../../components/ui';
import { XIcon } from '../../components/ui/Icons';

interface MarkAsPaidModalProps {
  payable: Payable;
  onClose: () => void;
  onSuccess?: () => void;
}

export const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({ payable, onClose, onSuccess }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPaymentAmount(payable.amount != null ? String(payable.amount) : '');
  }, [payable.amount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const amount = paymentAmount.trim() ? parseFloat(paymentAmount) : undefined;
      await markPayablePaid(payable.id, paymentDate, amount);
      showSuccess('Payable marked as paid.');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to mark as paid.');
    } finally {
      setSubmitting(false);
    }
  };

  const defaultAmountStr = payable.amount != null ? Number(payable.amount).toFixed(2) : '—';

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalHeader>
        <ModalTitle>Mark as paid</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-2"
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="mark-paid-date">Payment date</Label>
              <DateInput
                id="mark-paid-date"
                name="paymentDate"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="mark-paid-amount">Payment amount</Label>
              <NumberInput
                id="mark-paid-amount"
                name="paymentAmount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value != null ? String(e.target.value) : '')}
                placeholder={payable.amount != null ? String(payable.amount) : ''}
                min={0}
                step={0.01}
                allowDecimals
                showClearButton={false}
                helperText={`Leave empty to use payable amount (€${defaultAmountStr})`}
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} loading={submitting}>
            {submitting ? 'Saving...' : 'Mark as paid'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
